import { createSlice } from "@reduxjs/toolkit";

const INITIAL_STATE = {
  id: null,
  name: null,
  departments: [],
  facilityGroupCodeIds: [],
}

export const adminFacilitySlice = createSlice({
  name: "adminFacility",
  initialState: INITIAL_STATE,
  reducers: {
    activateDepartment: (state, action) => {
      const { departmentId } = action.payload
      const departmentIndex = state.departments.findIndex((department) => department.id === departmentId)
      state.departments[departmentIndex].active = true
    },
    activateFacility: (state) => {
      state.active = true
    },
    addDepartment: (state, action) => {
      state.departments.push(action.payload)
    },
    addMemberToDepartmentSegment: (state, action) => {
      const { departmentId, member, segmentName } = action.payload
      const department = state.departments.find((d) => d.id === departmentId)
      const segment = department.segments.find((s) => s.name === segmentName)

      segment.members.push(member)
    },
    deactivateDepartment: (state, action) => {
      const { departmentId } = action.payload
      const departmentIndex = state.departments.findIndex((department) => department.id === departmentId)
      state.departments[departmentIndex].active = false
    },
    deactivateFacility: (state) => {
      state.active = false
    },
    removeMemberFromDepartment: (state, action) => {
      const { departmentId, memberId } = action.payload
      const departmentIndex = state.departments.findIndex((department) => department.id === departmentId)

      const updatedSegments = state.departments[departmentIndex].segments.map((segment) => (
        {
          ...segment,
          members: segment.members.filter((member) => member.id !== memberId),
        }
      ))

      state.departments[departmentIndex].segments = updatedSegments
    },
    removeMemberFromDepartmentSegment: (state, action) => {
      const { departmentId, memberId, segmentName } = action.payload

      const department = state.departments.find((d) => d.id === departmentId)
      const segment = department.segments.find((s) => s.name === segmentName)

      segment.members = segment.members.filter((member) => member.id !== memberId)
    },
    setDepartmentName: (state, action) => {
      const { departmentId, name } = action.payload
      const departmentIndex = state.departments.findIndex((department) => department.id === departmentId)
      state.departments[departmentIndex].name = name
    },
    setFacility: (state, action) => {
      Object.assign(state, action.payload)
    },
    setAssignedFacilityGroupCodeIds: (state, action) => {
      state.facilityGroupCodeIds = action.payload
    },
    updateDepartment: (state, action) => {
      const {
        departmentId, name, externalIdentifier, listLastOnForms,
      } = action.payload
      const departmentIndex = state.departments.findIndex((department) => department.id === departmentId)
      const existingDepartment = state.departments[departmentIndex]

      const updatedDepartment = {
        ...existingDepartment,
        name: name ?? existingDepartment.name,
        externalIdentifier: externalIdentifier ?? existingDepartment.externalIdentifier,
        listLastOnForms: listLastOnForms ?? existingDepartment.listLastOnForms,
      }
      state.departments[departmentIndex] = updatedDepartment
    },
  },
})

export const {
  activateDepartment,
  activateFacility,
  addDepartment,
  addMemberToDepartmentSegment,
  deactivateDepartment,
  deactivateFacility,
  removeMemberFromDepartment,
  removeMemberFromDepartmentSegment,
  setDepartmentName,
  setFacility,
  setAssignedFacilityGroupCodeIds,
  updateDepartment,
} = adminFacilitySlice.actions

export const facilityActiveStatus = (state) => state.adminFacility.active

export const facilityDepartmentSegmentMemberIds = ({ departmentId, segmentName }) => (state) => {
  const department = state.adminFacility.departments.find((d) => d.id === departmentId)
  const segment = department.segments.find((s) => s.name === segmentName)

  return segment.members.map((member) => member.id)
}

export const facilityDepartmentMemberSegmentCounts = ({ departmentId }) => (state) => {
  const department = state.adminFacility.departments.find((d) => d.id === departmentId)

  return department.segments.reduce((counts, segment) => {
    segment.members.forEach((member) => {
      if (counts[member.id]) {
        counts[member.id] += 1
      } else {
        counts[member.id] = 1
      }
    })

    return counts
  }, {})
}

export const uniqueFacilityMembers = (state) => {
  const encounteredIds = new Set()

  return state.adminFacility.departments.reduce((departmentAcc, department) => (
    departmentAcc.concat(
      department.segments.reduce((segmentAcc, segment) => (
        segmentAcc.concat(
          segment.members.reduce((memberAcc, member) => {
            if (!encounteredIds.has(member.id)) {
              encounteredIds.add(member.id)
              return memberAcc.concat(member)
            }
            return memberAcc
          }, []),
        )
      ), []),
    )
  ), []).sort((member1, member2) => (
    member1.lastName.localeCompare(member2.lastName) || member1.firstName.localeCompare(member2.firstName)
  ))
}

export const uniqueFacilityMemberIds = (state) => {
  const uniqueMembers = uniqueFacilityMembers(state)
  return uniqueMembers.map((member) => member.id)
}

export const facilityHasDepartmentMembers = (state) => (
  uniqueFacilityMemberIds(state).length > 0
)

export const uniqueDepartmentMemberIds = ({ departmentId }) => (state) => {
  const department = state.adminFacility.departments.find((d) => d.id === departmentId)

  const idSet = department.segments.reduce((ids, segment) => {
    segment.members.forEach((member) => ids.add(member.id))

    return ids
  }, new Set())

  return Array.from(idSet)
}

export const facilityDepartments = (state) => state.adminFacility.departments

export const facilityId = (state) => state.adminFacility.id

export const facilityName = (state) => state.adminFacility.name

export const facilityGroupCodeIds = (state) => state.adminFacility.facilityGroupCodeIds

export default adminFacilitySlice.reducer
